import { useEffect, useState } from 'react';
import { Alert, Box, Button, FormControl, InputLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { AccessibleSelect as Select } from '../form/AccessibleSelectFields';
import { useIsGreek } from '../../context/LanguageContext';
import { resolveThermalSettings, THERMAL_PRESETS, THERMAL_UNITS, validThermalParameters } from '../config/thermalSettings';

const LABELS = {
  open_rack_glass_polymer: ['Ελεύθερος αερισμός — γυαλί/πολυμερές', 'Open rack — glass/polymer'],
  open_rack_glass_glass: ['Ελεύθερος αερισμός — γυαλί/γυαλί', 'Open rack — glass/glass'],
  close_mount_glass_glass: ['Περιορισμένος αερισμός — γυαλί/γυαλί', 'Close mount — glass/glass'],
  insulated_back_glass_polymer: ['Μονωμένη πίσω όψη — γυαλί/πολυμερές', 'Insulated back — glass/polymer'],
  freestanding: ['Ελεύθερος αερισμός', 'Freestanding'],
  semi_integrated: ['Περιορισμένος αερισμός', 'Semi-integrated'],
  insulated: ['Μονωμένη πίσω όψη', 'Insulated'],
  reference: ['Δημοσιευμένο ζεύγος Faiman', 'Published Faiman reference'],
  pvgis_csi_freestanding: ['PVGIS — c-Si, ελεύθερος αερισμός', 'PVGIS — c-Si, freestanding'],
  pvgis_csi_roof: ['PVGIS — c-Si, στέγη / ενσωματωμένο', 'PVGIS — c-Si, roof / building-integrated'],
};
export function thermalPresetLabel(preset, greek) { return LABELS[preset]?.[greek ? 0 : 1] || preset; }

// The equation each model solves, shown under the coefficients. Symbols are
// those of the coefficient labels above; E is the plane-of-array irradiance.
const sub = (base, index) => <>{base}<Box component="sub" sx={{ fontSize: '0.75em' }}>{index}</Box></>;
const EQUATIONS = {
  sapm: <>{sub('T', 'c')} = E · e<Box component="sup" sx={{ fontSize: '0.75em' }}>a + b·WS</Box> + {sub('T', 'a')} + (E / 1000) · ΔT</>,
  pvsyst: <>{sub('T', 'c')} = {sub('T', 'a')} + α · E · (1 − η) / ({sub('U', 'c')} + {sub('U', 'v')} · WS)</>,
  faiman: <>{sub('T', 'c')} = {sub('T', 'a')} + E / ({sub('U', '0')} + {sub('U', '1')} · WS)</>,
};

function ThermalEquation({ model, greek }) {
  const t = (gr, en) => greek ? gr : en;
  const legend = [
    t('Tc θερμοκρασία κυψέλης (°C)', 'Tc cell temperature (°C)'),
    t('Ta θερμοκρασία αέρα (°C)', 'Ta air temperature (°C)'),
    t('E ακτινοβολία στο επίπεδο (W/m²)', 'E plane-of-array irradiance (W/m²)'),
    t('WS ταχύτητα ανέμου (m/s)', 'WS wind speed (m/s)'),
    ...(model === 'pvsyst' ? [t('α = 0.9 απορρόφηση, η = 0.1 απόδοση πλαισίου', 'α = 0.9 absorption, η = 0.1 module efficiency')] : []),
  ];
  return <Box data-testid="thermal-equation" sx={{ mt: 1.5, mb: 1, pl: 1.5, py: 0.75, borderLeft: 3, borderColor: 'divider' }}>
    <Typography variant="body2" sx={{ fontFamily: '"Cambria Math", "STIX Two Math", Georgia, serif', fontStyle: 'italic', color: 'text.primary' }}>
      {EQUATIONS[model] || EQUATIONS.sapm}
    </Typography>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
      {legend.join(' · ')}
    </Typography>
  </Box>;
}

const secondaryActionSx = {
  typography: 'caption',
  textTransform: 'none',
  fontWeight: 500,
  minWidth: 0,
  px: 0.75,
  py: 0.25,
};

export default function PvThermalSettings({ model = 'sapm', settings, mounting, installation, onChange }) {
  const greek = useIsGreek();
  const t = (gr, en) => greek ? gr : en;
  const resolved = resolveThermalSettings(model, settings, mounting, installation);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(resolved.parameters);
  const signature = JSON.stringify(resolved.parameters);
  useEffect(() => { setEditing(false); setDraft(JSON.parse(signature)); }, [signature, model, resolved.mode]);
  const save = (entry) => onChange('thermalSettings', { ...settings, [model]: entry });
  const select = (label, value, change, options) => (
    <FormControl size="small" sx={{ minWidth: 240, flex: 1 }}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} onChange={(e) => change(e.target.value)} inputProps={{ 'aria-label': label }}>
        {options.map(([key, name]) => <MenuItem key={key} value={key}>{name}</MenuItem>)}
      </Select>
    </FormControl>
  );
  return <Box sx={{ pb: 2 }}>
    <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
      {select(t('Θερμική διάταξη', 'Thermal preset'), editing || resolved.mode === 'custom' ? 'custom' : resolved.preset, (preset) => {
        if (preset === 'custom') { setDraft(resolved.parameters); setEditing(true); }
        else { setEditing(false); save({ mode: 'preset', preset }); }
      }, [
        ...Object.keys(THERMAL_PRESETS[model]).map((key) => [key, thermalPresetLabel(key, greek)]),
        ['custom', t('Προσαρμοσμένο', 'Custom')],
      ])}
    </Stack>
    <Typography variant="body2" sx={{ mb: 1 }}>
      {editing || resolved.mode === 'custom' ? t('Χειροκίνητες τιμές για το επιλεγμένο μοντέλο.', 'Custom values for the selected model.')
        : thermalPresetLabel(resolved.preset, greek)}
    </Typography>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
      {!editing && resolved.mode === 'legacy'
        ? t('Τυχόν ξεχωριστές θερμικές διατάξεις ανά επιφάνεια διατηρούνται.', 'Existing per-surface thermal mounting settings are retained.')
        : t('Η επιλεγμένη θερμική ρύθμιση εφαρμόζεται σε όλες τις επιφάνειες του ΦΒ.', 'This thermal setting applies to all PV surfaces.')}
    </Typography>
    {model === 'faiman' && <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
      {t('Το Faiman χρησιμοποιεί τους παρακάτω συντελεστές. Η στήριξη δεν τους αλλάζει αυτόματα.', 'Faiman uses the coefficients below. Mounting does not change them automatically.')}
    </Typography>}
    {!resolved.valid && <Alert severity="error">{t('Μη έγκυρες αποθηκευμένες θερμικές παράμετροι. Διορθώστε τις πριν την εκτέλεση.', 'Invalid saved thermal parameters. Correct them before running.')}</Alert>}
    <Typography variant="subtitle2" sx={{ mb: 1 }}>{editing ? t('Παράμετροι πριν την εφαρμογή αλλαγών', 'Parameters before applying changes') : t('Ενεργές παράμετροι', 'Active parameters')}</Typography>
    <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap', mb: 1 }}>
      {Object.entries(resolved.parameters).map(([key, value]) => <Box key={key} sx={{ minWidth: 120 }}>
        <Typography variant="caption" color="text.secondary">{key === 'deltaT' ? 'ΔT' : key} {THERMAL_UNITS[key]}</Typography>
        <Typography variant="body2" fontWeight={700}>{value}</Typography>
      </Box>)}
    </Stack>
    <ThermalEquation model={model} greek={greek} />
    {!editing && <Button size="small" sx={secondaryActionSx} onClick={() => { setDraft(resolved.parameters); setEditing(true); }}>{t('Επεξεργασία συντελεστών', 'Edit coefficients')}</Button>}
    {editing && <Box sx={{ mt: 1, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
      <Typography variant="caption" display="block" sx={{ mb: 1 }}>{t('Οι αλλαγές εφαρμόζονται μόνο με «Εφαρμογή».', 'Changes take effect only after Apply.')}</Typography>
      <Stack direction="row" spacing={2}>
        {Object.keys(Object.values(THERMAL_PRESETS[model])[0]).map((key) => <TextField key={key} size="small" type="number"
          label={`${key === 'deltaT' ? 'ΔT' : key} ${THERMAL_UNITS[key]}`} value={draft[key] ?? ''}
          onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} inputProps={{ step: 'any' }} />)}
      </Stack>
      {!validThermalParameters(model, draft) && <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
        {t('Συμπληρώστε πεπερασμένες τιμές: a < 0, b ≤ 0, ΔT ≥ 0, Uc/U₀ > 0 και Uv/U₁ ≥ 0.', 'Enter finite values: a < 0, b ≤ 0, ΔT ≥ 0, Uc/U₀ > 0 and Uv/U₁ ≥ 0.')}
      </Typography>}
      <Button disabled={!validThermalParameters(model, draft)} onClick={() => {
        save({ mode: 'custom', parameters: Object.fromEntries(Object.entries(draft).map(([key, value]) => [key, Number(value)])) }); setEditing(false);
      }}>{t('Εφαρμογή', 'Apply')}</Button>
      <Button onClick={() => setEditing(false)}>{t('Ακύρωση', 'Cancel')}</Button>
    </Box>}
    {model === 'faiman' && <Box component="details" sx={{ mt: 1.5, typography: 'caption' }}>
      <Box component="summary" sx={{ cursor: 'pointer', color: 'primary.main' }}>{t('Ενδεικτικές τιμές & βοήθεια', 'Reference values & help')}</Box>
      <Typography variant="caption" component="p">U₀ = 25 W/(m²·K), U₁ = 6.84 W·s/(m³·K).</Typography>
      <Typography variant="caption" component="p">{t('Δημοσιευμένο ζεύγος από πλαίσια με γυαλί και πίσω φύλλο Tedlar. Δεν αποτελεί έτοιμο σετ για κάθε τύπο στέγης. Μεγαλύτερο U₀ σημαίνει περισσότερη ψύξη χωρίς άνεμο· μεγαλύτερο U₁ ισχυρότερη επίδραση ανέμου.', 'Published pair from glass/Tedlar modules, not a preset for every roof. Higher U₀ increases cooling without wind; higher U₁ increases wind cooling.')}</Typography>
      <Box component="a" href="https://pvpmc.sandia.gov/modeling-guide/2-dc-module-iv/module-temperature/faiman-module-temperature-model/" target="_blank" rel="noreferrer">Sandia / PVPMC</Box>
      <Button size="small" sx={secondaryActionSx} onClick={() => save({ mode: 'preset', preset: 'reference' })}>{t('Χρήση ζεύγους αναφοράς', 'Use reference pair')}</Button>
      <Typography variant="caption" component="p" sx={{ mt: 1 }}>
        {t('Τα σετ PVGIS αφορούν κρυσταλλικό πυρίτιο (c-Si): 26.9 / 6.2 για ελεύθερο αερισμό και 20 / 3.2 για στέγη ή ενσωματωμένα πλαίσια. Επιλέγουν μόνο τους θερμικούς συντελεστές, όχι το πλήρες μοντέλο παραγωγής PVGIS.', 'PVGIS presets apply to crystalline silicon (c-Si): 26.9 / 6.2 for freestanding modules and 20 / 3.2 for roof-added or building-integrated modules. They select thermal coefficients only, not the full PVGIS yield model.')}
      </Typography>
      <Box component="a" href="https://joint-research-centre.ec.europa.eu/photovoltaic-geographical-information-system-pvgis/general-information/data-sources-calculation-methods_en" target="_blank" rel="noreferrer">PVGIS / JRC</Box>
    </Box>}
  </Box>;
}
